import { useQueries, useQuery, type UseQueryResult } from '@tanstack/react-query';
import { get } from '@/shared/api/api-client';
import type { Patient } from '@/types/patient-api';

/** GET /api/Patients/{patientId} — full patient detail for the edit screen. */
export function usePatient(patientId: string | null | undefined) {
  return useQuery({
    queryKey: ['patient', patientId],
    queryFn: ({ signal }) => get<Patient>(`/api/Patients/${patientId}`, { signal }),
    enabled: !!patientId,
  });
}

/**
 * The same read for many patients at once (one cached query per id, the same key as
 * `usePatient`), for a list that must sort by name. Results follow the order of `patientIds`.
 */
export function usePatients(patientIds: readonly string[]) {
  return useQueries({
    queries: patientIds.map((patientId) => ({
      queryKey: ['patient', patientId],
      queryFn: ({ signal }: { signal: AbortSignal }) => get<Patient>(`/api/Patients/${patientId}`, { signal }),
      enabled: !!patientId,
    })),
    combine: combinePatients,
  });
}

/** One plain entry per patient. The result is structurally shared, so its identity changes only
 * when a status or a patient field changes (a refetch with a new name or MRN does change it). */
function combinePatients(results: UseQueryResult<Patient>[]) {
  return results.map((q) => ({ isPending: q.isPending, isError: q.isError, data: q.data }));
}
